import { handleOpenNewAccount } from 'common/navUtils';
import ChevronLeftIcon from 'components/common/icons/ChevronLeft';
import React from 'react';
import { Dispatch, UnknownAction } from 'redux';
import { AccountType, TransferFlow, TransferTypeValue } from 'utils/constants';
import { validateManualForm } from 'utils/validations';
import {
  CTAVariant,
  CTAVariants,
} from '@trp-beacon-ui-kit/foundation/dist/_common/types/cta-variant';
import { FooterSlot } from '../components/StepFooter/StepFooter';
import {
  addStepToHistory as addStepAction,
  popLastStepFromHistory,
  setCurrentStep,
} from '../transferSlice';
import ChooseAccountType from './chooseAccountType/ChooseAccountType';
import ExternalAccountIncomplete from './externalAccountIncomplete/ExternalAccountIncomplete';
import InitiateTransfer from './initiateTransfer/InitiateTransfer';
import ManualPartialTransfer from './manualPartialTransfer/ManualPartialTransfer';
import ManualTransfer from './manualTransfer/ManualTransfer';
import PartialTransfer from './partialTransfer/PartialTransfer';
import RequiresPaperwork from './requiresPaperwork/RequiresPaperwork';
import ReviewTransfer from './reviewTransfer/ReviewTransfer';
import SelectExternalAccount from './selectExternalAccount/selectExternalAccount';
import SelectInternalAccount from './selectInternalAccount/selectInternalAccount';
import SplashPage from './splashPage/SplashPage';

export enum TransferFlowStep {
  CHOOSE_ACCOUNT_TYPE = 1,
  SPLASH_PAGE,
  INITIATE_TRANSFER,
  SELECT_EXTERNAL_ACCOUNT,
  PARTIAL_TRANSFER,
  MANUAL_TRANSFER,
  MANUAL_PARTIAL_TRANSFER,
  SELECT_INTERNAL_ACCOUNT,
  REVIEW_TRANSFER,
  REQUIRES_PAPERWORK,
  EXTERNAL_ACCOUNT_INCOMPLETE,
}

/** All account types used by ManualTransfer and ExternalAccountIncomplete steps */
const ALL_ACCOUNT_TYPES = [
  AccountType.INDIVIDUAL,
  AccountType.JOINT_TENANT,
  AccountType.TRUST,
  AccountType.ESTATE,
  AccountType.UGMA_UTMA,
  AccountType.CORPORATE,
  AccountType.TRADITIONAL_IRA,
  AccountType.ROLLOVER_IRA,
  AccountType.ROTH_IRA,
  AccountType.ROTH_ROLLOVER_IRA,
  AccountType.SIMPLE_IRA,
  AccountType.SEP_IRA,
  AccountType.INHERITED_IRA,
  AccountType.ROTH_INHERITED_IRA,
  AccountType.INDIVIDUAL_401K,
  AccountType.OTHER,
];

interface FooterButton {
  label: string;
  handler: () => void;
  slot: FooterSlot;
  variant: CTAVariant;
  icon?: React.FC;
  enabled?: boolean;
}

/** Creates a standard "Back" button that navigates to the previous step */
const createBackButton = (goToLastPage: () => void, enabled?: boolean): FooterButton => ({
  label: 'Back',
  handler: goToLastPage,
  slot: FooterSlot.Left,
  variant: CTAVariants.Tertiary,
  icon: ChevronLeftIcon,
  enabled,
});

/** Creates a standard "Exit" button that shows the exit modal */
const createExitButton = (setShowExitModal: (show: boolean) => void): FooterButton => ({
  label: 'Exit',
  handler: () => setShowExitModal(true),
  slot: FooterSlot.Right,
  variant: CTAVariants.Secondary,
});

export const getTransferFlowSteps = (
  dispatch: Dispatch<UnknownAction>,
  transferState: {
    stepState: { isStepValid: boolean; setIsStepValid: (isValid: boolean) => void };
    cancelUrl: string;
    nasuUrl: string;
    docusignUrl: string;
    transferType?: TransferTypeValue;
    transferFlow?: TransferFlow | null;
    showErrorNotification: {
      showValidation: boolean;
      setShowValidation: (isValid: boolean) => void;
    };
    showExitModal: {
      showExitModal: boolean;
      setShowExitModal: (isValid: boolean) => void;
    };
    showErrorModal: {
      isAccountAvailable: boolean;
      setIsAccountAvailable: (isValid: boolean) => void;
      showValidationModal: boolean;
      setShowValidationModal: (isValid: boolean) => void;
    };
    lastStep: number;
    isOpeningNewInternalAccount?: boolean;
    selectedAccountTypeId: number | null;
    isCustomerWebView: boolean;
  },
  sendAssetTransferPayload: () => void = () => {
    // Intentionally left empty
  },
) => {
  const {
    nasuUrl,
    transferType,
    transferFlow,
    stepState: { isStepValid, setIsStepValid },
    showErrorNotification: { showValidation, setShowValidation },
    showExitModal: { setShowExitModal },
    showErrorModal: {
      showValidationModal,
      setShowValidationModal,
      isAccountAvailable,
      setIsAccountAvailable,
    },
    lastStep,
    isOpeningNewInternalAccount,
    docusignUrl,
    selectedAccountTypeId,
    isCustomerWebView,
  } = transferState;

  const goToStep = (step: number) => {
    dispatch(setCurrentStep(step));
  };

  const goToLastPage = () => {
    dispatch(popLastStepFromHistory());
    setShowValidation(false);
    goToStep(lastStep);
  };

  const addStepToHistory = (page: number) => {
    dispatch(addStepAction(page));
  };

  const backButton = createBackButton(goToLastPage);
  const backButtonEnabled = createBackButton(goToLastPage, true);
  const exitButton = createExitButton(setShowExitModal);

  return {
    [TransferFlowStep.CHOOSE_ACCOUNT_TYPE]: {
      header: 'Choose the account type you want to transfer from',
      component: (
        <ChooseAccountType
          commonAccountTypes={[
            AccountType.INDIVIDUAL,
            AccountType.JOINT_TENANT,
            AccountType.TRADITIONAL_IRA,
            AccountType.ROLLOVER_IRA,
            AccountType.ROTH_IRA,
            AccountType.ROTH_ROLLOVER_IRA,
          ]}
          additionalAccountTypes={[
            AccountType.TRUST,
            AccountType.ESTATE,
            AccountType.UGMA_UTMA,
            AccountType.INDIVIDUAL_401K,
            AccountType.SIMPLE_IRA,
            AccountType.SEP_IRA,
            AccountType.INHERITED_IRA,
            AccountType.ROTH_INHERITED_IRA,
            AccountType.CORPORATE,
            AccountType.OTHER,
          ]}
          setIsStepValid={setIsStepValid}
          showValidation={showValidation}
          setShowValidation={setShowValidation}
          showValidationModal={showValidationModal}
          setShowValidationModal={setShowValidationModal}
          setIsAccountAvailable={setIsAccountAvailable}
        />
      ),
      footer: [
        {
          label: 'Cancel',
          handler: () => {
            setShowExitModal(true);
          },
          slot: FooterSlot.Left,
          variant: CTAVariants.Tertiary,
          enabled: true,
        },
        {
          label: 'Next',
          handler: () => {
            if (isStepValid) {
              if (isAccountAvailable) {
                addStepToHistory(TransferFlowStep.CHOOSE_ACCOUNT_TYPE);
                goToStep(TransferFlowStep.SPLASH_PAGE);
                setShowValidation(false);
                setShowValidationModal(false);
              } else {
                setShowValidation(false);
                setShowValidationModal(true);
              }
            } else {
              setShowValidation(true);
            }
          },
          slot: FooterSlot.Right,
          variant: CTAVariants.Primary,
          enabled: true,
        },
      ],
    },
    [TransferFlowStep.SPLASH_PAGE]: {
      header: 'Transferring assets to T. Rowe Price',
      component: <SplashPage />,
      footer: [
        {
          label: 'Back',
          handler: goToLastPage,
          slot: FooterSlot.Left,
          variant: CTAVariants.Tertiary,
          icon: ChevronLeftIcon,
          enabled: true,
        },
        {
          label: 'Start Transfer Process',
          handler: () => {
            addStepToHistory(TransferFlowStep.SPLASH_PAGE);
            goToStep(TransferFlowStep.INITIATE_TRANSFER);
          },
          slot: FooterSlot.Right,
          variant: CTAVariants.Primary,
        },
      ],
    },
    [TransferFlowStep.INITIATE_TRANSFER]: {
      header: 'Where do you want to transfer assets from?',
      component: <InitiateTransfer setIsStepValid={setIsStepValid} />,
      footer: [
        {
          label: 'Back',
          handler: () => {
            goToLastPage();
            if ((window as any).fastLink?.close) {
              (window as any).fastLink.close();
            }
          },
          slot: FooterSlot.Left,
          variant: CTAVariants.Tertiary,
          icon: ChevronLeftIcon,
        },
        exitButton,
      ],
    },
    [TransferFlowStep.SELECT_EXTERNAL_ACCOUNT]: {
      header: 'Select the account to transfer assets from',
      component: (
        <SelectExternalAccount
          setIsStepValid={setIsStepValid}
          showValidation={showValidation}
          setShowValidation={setShowValidation}
        />
      ),
      footer: [
        backButton,
        exitButton,
        {
          label: 'Continue',
          handler: () => {
            if (!isStepValid) {
              setShowValidation(true);
              return;
            }

            addStepToHistory(TransferFlowStep.SELECT_EXTERNAL_ACCOUNT);

            switch (transferFlow) {
              case TransferFlow.Manual:
                goToStep(TransferFlowStep.MANUAL_TRANSFER);
                break;

              case TransferFlow.AutomatedIncomplete:
                goToStep(TransferFlowStep.EXTERNAL_ACCOUNT_INCOMPLETE);
                break;

              default:
                if (transferType === TransferTypeValue.PartialTransfer) {
                  goToStep(TransferFlowStep.PARTIAL_TRANSFER);
                } else if (transferType === TransferTypeValue.FullTransfer) {
                  goToStep(TransferFlowStep.SELECT_INTERNAL_ACCOUNT);
                  setIsStepValid(false);
                }
                break;
            }

            setShowValidation(false);
          },
          slot: FooterSlot.Right,
          variant: CTAVariants.Primary,
          enabled: true,
        },
      ],
    },
    [TransferFlowStep.PARTIAL_TRANSFER]: {
      header: 'Let us know what investments you\u2019re transferring',
      component: (
        <PartialTransfer
          setIsStepValid={setIsStepValid}
          showValidation={showValidation}
          setShowValidation={setShowValidation}
        />
      ),
      footer: [
        backButton,
        exitButton,
        {
          label: 'Next',
          handler: () => {
            const form = document.getElementById(
              'partialTransferForm',
            ) as HTMLFormElement;
            if (form.checkValidity() && isStepValid) {
              addStepToHistory(TransferFlowStep.PARTIAL_TRANSFER);
              goToStep(TransferFlowStep.SELECT_INTERNAL_ACCOUNT);
              setShowValidation(false);
            } else {
              setShowValidation(true);
            }
          },
          slot: FooterSlot.Right,
          variant: CTAVariants.Primary,
        },
      ],
    },
    [TransferFlowStep.MANUAL_TRANSFER]: {
      header: 'Tell us more about the account you\u2019re transferring from',
      component: (
        <ManualTransfer
          accountTypes={ALL_ACCOUNT_TYPES}
          setIsStepValid={setIsStepValid}
          showValidation={showValidation}
          setShowValidation={setShowValidation}
        />
      ),
      footer: [
        backButton,
        exitButton,
        {
          label: 'Continue',
          handler: () => {
            const form = document.getElementById(
              'manual-transfer-form',
            ) as HTMLFormElement;
            const isValid = validateManualForm(form);
            if (isValid) {
              setShowValidation(false);
              if (transferType === TransferTypeValue.PartialTransfer) {
                addStepToHistory(TransferFlowStep.MANUAL_TRANSFER);
                goToStep(TransferFlowStep.MANUAL_PARTIAL_TRANSFER);
              } else if (transferType === TransferTypeValue.FullTransfer) {
                addStepToHistory(TransferFlowStep.MANUAL_TRANSFER);
                goToStep(TransferFlowStep.SELECT_INTERNAL_ACCOUNT);
              }
            } else {
              setShowValidation(true);
            }
          },
          slot: FooterSlot.Right,
          variant: CTAVariants.Primary,
        },
      ],
    },
    [TransferFlowStep.MANUAL_PARTIAL_TRANSFER]: {
      header: 'Let us know what investments you\u2019re transferring',
      component: (
        <ManualPartialTransfer
          setIsStepValid={setIsStepValid}
          showValidation={showValidation}
          setShowValidation={setShowValidation}
        />
      ),
      footer: [
        backButton,
        exitButton,
        {
          label: 'Next',
          handler: () => {
            const form = document.getElementById(
              'manualPartialTransferForm',
            ) as HTMLFormElement;
            if (form.checkValidity() && isStepValid) {
              addStepToHistory(TransferFlowStep.MANUAL_PARTIAL_TRANSFER);
              goToStep(TransferFlowStep.SELECT_INTERNAL_ACCOUNT);
              setShowValidation(false);
            } else {
              setShowValidation(true);
            }
          },
          slot: FooterSlot.Right,
          variant: CTAVariants.Primary,
        },
      ],
    },
    [TransferFlowStep.SELECT_INTERNAL_ACCOUNT]: {
      header: 'Select the T. Rowe Price account to transfer your assets to',
      component: (
        <SelectInternalAccount
          setIsStepValid={setIsStepValid}
          showValidation={showValidation}
          setShowValidation={setShowValidation}
        />
      ),
      footer: [
        backButtonEnabled,
        exitButton,
        {
          label: isOpeningNewInternalAccount
            ? 'Go to Open a New Account'
            : 'Go to Review Page',
          handler: () => {
            if (isStepValid) {
              if (!isOpeningNewInternalAccount) {
                addStepToHistory(TransferFlowStep.SELECT_INTERNAL_ACCOUNT);
                goToStep(TransferFlowStep.REVIEW_TRANSFER);
                setShowValidation(false);
                setIsStepValid(false);
              } else {
                handleOpenNewAccount(nasuUrl, selectedAccountTypeId);
              }
            } else {
              setShowValidation(true);
            }
          },
          slot: FooterSlot.Right,
          variant: CTAVariants.Primary,
        },
      ],
    },
    [TransferFlowStep.REVIEW_TRANSFER]: {
      header: 'Review your asset transfer',
      component: <ReviewTransfer />,
      footer: [
        backButtonEnabled,
        exitButton,
        {
          label: 'Continue to DocuSign',
          handler: () => {
            sendAssetTransferPayload();
          },
          slot: FooterSlot.Right,
          variant: CTAVariants.Primary,
          enabled: !isCustomerWebView,
        },
      ],
    },
    [TransferFlowStep.REQUIRES_PAPERWORK]: {
      header: 'Your transfer requires paperwork',
      component: <RequiresPaperwork />,
      footer: [
        {
          label: 'Continue to DocuSign',
          handler: () => {
            window.location.href = docusignUrl;
          },
          slot: FooterSlot.Right,
          variant: CTAVariants.Primary,
        },
      ],
    },
    [TransferFlowStep.EXTERNAL_ACCOUNT_INCOMPLETE]: {
      header: 'Confirm missing account details',
      component: (
        <ExternalAccountIncomplete
          accountTypes={ALL_ACCOUNT_TYPES}
          setIsStepValid={setIsStepValid}
          showValidation={showValidation}
          setShowValidation={setShowValidation}
        />
      ),
      footer: [
        backButtonEnabled,
        {
          label: 'Continue',
          handler: () => {
            const form = document.getElementById(
              'automated-incomplete-transfer-form',
            ) as HTMLFormElement;
            const isValid = validateManualForm(form);
            if (isValid) {
              addStepToHistory(TransferFlowStep.EXTERNAL_ACCOUNT_INCOMPLETE);
              if (transferType === TransferTypeValue.PartialTransfer) {
                goToStep(TransferFlowStep.PARTIAL_TRANSFER);
              } else {
                goToStep(TransferFlowStep.SELECT_INTERNAL_ACCOUNT);
              }
              setShowValidation(false);
              setIsStepValid(false);
            } else {
              setShowValidation(true);
            }
          },
          slot: FooterSlot.Right,
          variant: CTAVariants.Primary,
        },
      ],
    },
  };
};
